import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function SuggestProduct() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const presetCategory = searchParams.get("category") || "";
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    kind: "libro",
    category: presetCategory,
    age_min: 3,
    age_max: 6,
    brand_or_author: "",
    description: "",
    utility_note: "",
    store_link: "",
    privacy_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/products/categories").then(({ data }) => {
      setCategories(data);
      if (!form.category && data[0]) setForm((f) => ({ ...f, category: data[0].slug }));
    });
    // eslint-disable-next-line
  }, []);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <div className="card-surface p-8">
          <p className="label-eyebrow text-[#C05C45]">Accesso richiesto</p>
          <h1 className="mt-2 font-display text-3xl text-[#1A362D]">Accedi per suggerire un prodotto</h1>
          <p className="mt-3 font-ui text-sm text-[#5C6B64]">
            Per garantire qualità della community, solo i genitori autenticati possono suggerire libri o giochi.
          </p>
          <button
            onClick={() => {
              sessionStorage.setItem("return_to", "/suggerisci-prodotto");
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            data-testid="suggest-product-login-btn"
            className="mt-6 btn-primary"
          >
            Accedi con Google
          </button>
        </div>
      </div>
    );
  }

  const setField = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) return toast.error("Accetta la Privacy Policy");
    if (form.name.trim().length < 2) return toast.error("Inserisci il nome del prodotto");
    if (form.description.trim().length < 10) return toast.error("Descrizione troppo breve (min 10 caratteri)");
    if (!form.category) return toast.error("Seleziona una categoria");
    if (Number(form.age_max) < Number(form.age_min)) return toast.error("Età max deve essere ≥ età min");

    setSubmitting(true);
    try {
      await api.post("/product-suggestions", {
        ...form,
        age_min: Number(form.age_min),
        age_max: Number(form.age_max),
      });
      toast.success("Grazie! La tua segnalazione è stata inviata ai moderatori.");
      navigate("/prodotti");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 font-ui text-[#1A362D]">
      <p className="label-eyebrow text-[#C05C45]">Contribuisci alla community</p>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">Consiglia materiale educativo</h1>
      <p className="mt-3 text-sm text-[#5C6B64] leading-relaxed">
        Hai trovato un libro o un gioco che ti è stato davvero utile? Segnalalo: i moderatori lo verificheranno e
        sarà visibile a tutti i genitori della community. Puoi includere il link al tuo store di fiducia (Amazon o altro).
      </p>

      <form onSubmit={submit} className="mt-8 card-surface p-6 sm:p-8 space-y-5" data-testid="suggest-product-form">
        <div>
          <label className="text-sm font-medium">Nome prodotto *</label>
          <input data-testid="sp-name" value={form.name} onChange={setField("name")} required minLength={2} maxLength={200}
            className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Tipo *</label>
            <select data-testid="sp-kind" value={form.kind} onChange={setField("kind")}
              className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]">
              <option value="libro">📖 Libro</option>
              <option value="gioco">🧩 Gioco educativo</option>
              <option value="app">📱 App</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Categoria *</label>
            <select data-testid="sp-category" value={form.category} onChange={setField("category")}
              className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]">
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.emoji} {c.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Autore o brand</label>
          <input data-testid="sp-brand" value={form.brand_or_author} onChange={setField("brand_or_author")} maxLength={200}
            placeholder="Es. Anna Llenas — Gribaudo"
            className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Età minima *</label>
            <input data-testid="sp-age-min" type="number" min={0} max={18} value={form.age_min} onChange={setField("age_min")} required
              className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]" />
          </div>
          <div>
            <label className="text-sm font-medium">Età massima *</label>
            <input data-testid="sp-age-max" type="number" min={0} max={18} value={form.age_max} onChange={setField("age_max")} required
              className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]" />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Descrizione *</label>
          <textarea data-testid="sp-description" value={form.description} onChange={setField("description")} required minLength={10} maxLength={2000} rows={3}
            placeholder="Di cosa parla / di che gioco si tratta"
            className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]" />
        </div>

        <div>
          <label className="text-sm font-medium">Perché aiuta i genitori</label>
          <textarea data-testid="sp-utility" value={form.utility_note} onChange={setField("utility_note")} maxLength={1000} rows={2}
            placeholder="Es. Aiuta a riconoscere le emozioni; usato dai logopedisti…"
            className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]" />
        </div>

        <div>
          <label className="text-sm font-medium">Link store (opzionale)</label>
          <input data-testid="sp-store-link" type="url" value={form.store_link} onChange={setField("store_link")} maxLength={600}
            placeholder="https://www.amazon.it/…  oppure URL ufficiale del prodotto"
            className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#C05C45]" />
          <p className="mt-1 text-xs text-[#8A9691]">Accettiamo qualsiasi store (Amazon, IBS, La Feltrinelli, sito ufficiale dell'editore…). Se non hai un link, lasciamo una ricerca Amazon automatica.</p>
        </div>

        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <input type="checkbox" data-testid="sp-privacy" checked={form.privacy_consent} onChange={setField("privacy_consent")} className="mt-1 w-4 h-4 accent-[#C05C45]" />
          <span>Accetto la <Link to="/info/privacy" className="underline text-[#C05C45]">Privacy Policy</Link> e i <Link to="/info/termini" className="underline text-[#C05C45]">Termini di Servizio</Link>.</span>
        </label>

        <button type="submit" disabled={submitting} data-testid="sp-submit" className="btn-primary disabled:opacity-50">
          {submitting ? "Invio…" : "Invia segnalazione"}
        </button>
      </form>
    </div>
  );
}
